/**
 * Lookup Admin Page
 * Admin interface for managing configurable drop-down values
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Search, ChevronRight, Settings2 } from 'lucide-react';
import { LookupType } from '@bizsolutions/shared';

interface LookupValue {
  id: string;
  lookupType: string;
  code: string;
  label: string;
  description: string | null;
  parentId: string | null;
  parentLabel: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
  childCount: number;
}

interface LookupTypeInfo {
  lookupType: string;
  valueCount: number;
  activeCount: number;
}

const LOOKUP_TYPE_LABELS: Record<string, string> = {
  [LookupType.INDUSTRY]: 'Industry',
  [LookupType.DEAL_TYPE]: 'Deal Type',
  [LookupType.TICKET_CATEGORY]: 'Ticket Category',
  [LookupType.EXPENSE_TAX_CATEGORY]: 'Tax Category',
  [LookupType.DOCUMENT_CATEGORY]: 'Document Category',
  [LookupType.ACTIVITY_CATEGORY]: 'Activity Category',
  [LookupType.MATTER_TYPE]: 'Matter Type',
  [LookupType.MATTER_SUBTYPE]: 'Matter Subtype',
  [LookupType.JURISDICTION]: 'Jurisdiction',
  [LookupType.PRIORITY_LEVEL]: 'Priority Level',
};

export function LookupAdminPage() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewTypeDialogOpen, setIsNewTypeDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<LookupValue | null>(null);
  const [formData, setFormData] = useState({
    lookupType: '',
    code: '',
    label: '',
    description: '',
    sortOrder: 0,
    parentId: '',
  });
  const [newTypeName, setNewTypeName] = useState('');

  // Fetch lookup types
  const { data: typesData } = useQuery({
    queryKey: ['lookupTypes'],
    queryFn: () => api.getLookupTypes(),
  });
  const lookupTypes = (typesData?.data || []) as LookupTypeInfo[];

  // Fetch values for selected type
  const { data: valuesData } = useQuery({
    queryKey: ['lookupValues', selectedType, search],
    queryFn: () =>
      api.getLookupValues({
        lookupType: selectedType || undefined,
        search: search || undefined,
        limit: 100,
      }),
    enabled: !!selectedType,
  });
  const lookupValues = (valuesData?.data || []) as LookupValue[];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createLookupValue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lookupTypes'] });
      queryClient.invalidateQueries({ queryKey: ['lookupValues'] });
      setIsDialogOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateLookupValue(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lookupValues'] });
      setIsDialogOpen(false);
      setEditingValue(null);
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLookupValue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lookupTypes'] });
      queryClient.invalidateQueries({ queryKey: ['lookupValues'] });
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.updateLookupValue(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lookupValues'] });
      queryClient.invalidateQueries({ queryKey: ['lookupTypes'] });
    },
  });

  function resetForm() {
    setFormData({ lookupType: '', code: '', label: '', description: '', sortOrder: 0, parentId: '' });
  }

  function handleAdd() {
    setEditingValue(null);
    setFormData({
      lookupType: selectedType || '',
      code: '',
      label: '',
      description: '',
      sortOrder: lookupValues.length,
      parentId: '',
    });
    setIsDialogOpen(true);
  }

  function handleEdit(value: LookupValue) {
    setEditingValue(value);
    setFormData({
      lookupType: value.lookupType,
      code: value.code,
      label: value.label,
      description: value.description || '',
      sortOrder: value.sortOrder,
      parentId: value.parentId || '',
    });
    setIsDialogOpen(true);
  }

  function handleSubmit() {
    if (editingValue) {
      updateMutation.mutate({
        id: editingValue.id,
        data: {
          label: formData.label,
          description: formData.description || null,
          sortOrder: formData.sortOrder,
          parentId: formData.parentId || null,
        },
      });
    } else {
      createMutation.mutate({
        lookupType: formData.lookupType,
        code: formData.code,
        label: formData.label,
        description: formData.description || null,
        sortOrder: formData.sortOrder,
        parentId: formData.parentId || null,
      });
    }
  }

  function handleAddNewType() {
    if (!newTypeName.trim()) return;
    const code = newTypeName.trim().toLowerCase().replace(/\s+/g, '_');
    setSelectedType(code);
    setIsNewTypeDialogOpen(false);
    setNewTypeName('');
    // Open the add value dialog for this new type
    setEditingValue(null);
    setFormData({ lookupType: code, code: '', label: '', description: '', sortOrder: 0, parentId: '' });
    setIsDialogOpen(true);
  }

  // Combine known types with existing types from DB
  const allTypes = new Set([
    ...Object.values(LookupType),
    ...lookupTypes.map((t) => t.lookupType),
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lookup Table Admin</h1>
          <p className="text-muted-foreground">
            Manage configurable drop-down values used throughout the application
          </p>
        </div>
        <Button onClick={() => setIsNewTypeDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Lookup Type
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: Type List */}
        <div className="col-span-4 border rounded-lg">
          <div className="p-3 border-b">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              Lookup Types
            </h3>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {Array.from(allTypes).map((type) => {
              const info = lookupTypes.find((t) => t.lookupType === type);
              return (
                <button
                  key={type}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-muted transition-colors flex items-center justify-between ${
                    selectedType === type ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                  }`}
                  onClick={() => {
                    setSelectedType(type);
                    setSearch('');
                  }}
                >
                  <div>
                    <div className="font-medium">
                      {LOOKUP_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                    <div className="text-xs text-muted-foreground">{type}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {info && (
                      <Badge variant="secondary" className="text-xs">
                        {info.activeCount}/{info.valueCount}
                      </Badge>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              );
            })}
            {allTypes.size === 0 && (
              <div className="p-6 text-center text-muted-foreground text-sm">
                No lookup types yet. Create one to get started.
              </div>
            )}
          </div>
        </div>

        {/* Right: Values Table */}
        <div className="col-span-8 border rounded-lg">
          {selectedType ? (
            <>
              <div className="p-3 border-b flex items-center justify-between">
                <h3 className="font-semibold">
                  {LOOKUP_TYPE_LABELS[selectedType] || selectedType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search values..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 h-9 w-48"
                    />
                  </div>
                  <Button size="sm" onClick={handleAdd}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Value
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead className="text-center">Order</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lookupValues.map((value) => (
                    <TableRow key={value.id}>
                      <TableCell className="font-mono text-xs">{value.code}</TableCell>
                      <TableCell className="font-medium">{value.label}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                        {value.description || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {value.parentLabel || '-'}
                        {value.childCount > 0 && (
                          <Badge variant="outline" className="ml-1 text-xs">
                            {value.childCount} children
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{value.sortOrder}</TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() =>
                            toggleActiveMutation.mutate({ id: value.id, isActive: !value.isActive })
                          }
                          disabled={value.isSystem}
                        >
                          <Badge variant={value.isActive ? 'default' : 'secondary'}>
                            {value.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(value)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {!value.isSystem && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm('Delete this value?')) deleteMutation.mutate(value.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {lookupValues.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No values found. Add one to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              Select a lookup type from the left to manage its values
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Value Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingValue ? 'Edit Value' : 'Add Value'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingValue && (
              <>
                <div>
                  <Label>Lookup Type</Label>
                  <Input value={formData.lookupType} disabled className="bg-muted" />
                </div>
                <div>
                  <Label>Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., technology"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Machine-readable key (no spaces)</p>
                </div>
              </>
            )}
            <div>
              <Label>Label</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Technology"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Parent ID (for cascading)</Label>
                <Input
                  value={formData.parentId}
                  onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                  placeholder="Optional parent UUID"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingValue ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Type Dialog */}
      <Dialog open={isNewTypeDialogOpen} onOpenChange={setIsNewTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Lookup Type</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Type Name</Label>
            <Input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="e.g., Matter Status"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Will be converted to a code like &quot;matter_status&quot;
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewTypeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNewType}>Create & Add Values</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
